'use strict';

import { Entity } from '../Entity';
import { User } from '../User/User';
import { Utilities } from '../../Utilities/Utilities';
import { Ind } from '../Ind/Ind';
import { CommissionStatementMapping } from './CommissionStatementMapping';
import type { CommissionStatementMappingFieldName } from '../../Repositories/CommissionStatement/CommissionStatementMappingRepository';
import type { CommissionStatementStatus } from '../../Repositories/CommissionStatement/CommissionStatementRepository';

/**
 * Commission Statement Entity
 *
 * Represents a commission statement document that contains commission data
 * from insurance providers. This entity handles the parsing, mapping, and
 * processing of commission statement files.
 */
export class CommissionStatement extends Entity {
  /** Unique identifier for the commission statement */
  protected id: number;

  /** Human-readable name for the commission statement */
  protected name: string;

  /** Associated mapping configuration for parsing the statement */
  protected commissionStatementMapping: CommissionStatementMapping;

  /** Date and time when the statement was created */
  protected dateTimeCreated: Date;

  /** User who owns this commission statement */
  protected user: User;

  /** Date of the commission statement period */
  protected date: Date;

  /** Excel sheet number if applicable */
  protected sheet: number | null = null;

  /** File path or identifier for the stored file */
  protected file: string;

  /** Original filename of the uploaded file */
  protected fileName: string;

  /** Current processing status of the statement */
  protected status: CommissionStatementStatus;

  /** Raw data from the commission statement file */
  protected data: Array<Array<any>>;

  /** Formatted/processed data from the statement */
  protected dataFormatted: any;

  /** Array of row numbers to ignore during processing */
  protected ignoredRows: Array<number> | null = null;

  /** Row number containing column headers */
  protected headerRow: number | null = null;

  /**
   * Constructor
   *
   * Initializes a new CommissionStatement instance
   */
  constructor() {
    super();
  }

  /**
   * Hydrate the entity with data from API response
   *
   * @param data - Raw data object from API
   */
  public hydrate(data: any) {
    // Basic properties
    this.id = data.id;
    this.name = data.name;
    this.file = data.file;
    this.fileName = data.fileName;
    this.status = data.status;
    this.sheet = data.sheet;
    this.data = data.data;
    this.dataFormatted = data.dataFormatted;
    this.headerRow = data.headerRow;
    this.ignoredRows = data.ignoredRows;

    // Hydrate related User entity
    if (data.user) {
      const user = new User();
      user.hydrate(data.user);
      this.user = user;
    }

    // Hydrate related CommissionStatementMapping entity
    if (data.commissionStatementMapping) {
      const commissionStatementMapping = new CommissionStatementMapping();
      commissionStatementMapping.hydrate(data.commissionStatementMapping);
      this.commissionStatementMapping = commissionStatementMapping;
    }

    // Convert date strings to Date objects
    if (data.dateTimeCreated) {
      const dateTimeCreated = Utilities.dateTimeStringToDateTime(data.dateTimeCreated);
      this.dateTimeCreated = dateTimeCreated;
    }

    if (data.date) {
      const date = Utilities.dateTimeStringToDate(data.date);
      this.date = date;
    }
  }

  // Getter and Setter Methods

  /**
   * Get the commission statement ID
   *
   * @returns The unique identifier
   */
  public getId(): number {
    return this.id;
  }

  /**
   * Get the commission statement name
   *
   * @returns The statement name
   */
  public getName(): string {
    return this.name;
  }

  /**
   * Set the commission statement name
   *
   * @param name - The statement name
   * @returns This instance for method chaining
   */
  public setName(name: string): this {
    this.name = name;
    return this;
  }

  /**
   * Get the Excel sheet number
   *
   * @returns The sheet number or null if not applicable
   */
  public getSheet(): number | null {
    return this.sheet;
  }

  /**
   * Set the Excel sheet number
   *
   * @param sheet - The sheet number or null
   * @returns This instance for method chaining
   */
  public setSheet(sheet: number | null): this {
    this.sheet = sheet;
    return this;
  }

  /**
   * Get the file path or identifier
   *
   * @returns The file identifier
   */
  public getFile(): string {
    return this.file;
  }

  /**
   * Set the file path or identifier
   *
   * @param file - The file identifier
   * @returns This instance for method chaining
   */
  public setFile(file: string): this {
    this.file = file;
    return this;
  }

  /**
   * Get the original filename
   *
   * @returns The filename
   */
  public getFileName(): string {
    return this.fileName;
  }

  /**
   * Set the original filename
   *
   * @param fileName - The filename
   * @returns This instance for method chaining
   */
  public setFileName(fileName: string): this {
    this.fileName = fileName;
    return this;
  }

  /**
   * Get the current processing status
   *
   * @returns The status
   */
  public getStatus(): CommissionStatementStatus {
    return this.status;
  }

  /**
   * Set the processing status
   *
   * @param status - The new status
   * @returns This instance for method chaining
   */
  public setStatus(status: CommissionStatementStatus): this {
    this.status = status;
    return this;
  }

  /**
   * Get the raw data from the commission statement
   *
   * @returns The raw data array
   */
  public getData(): any {
    return this.data;
  }

  /**
   * Set the raw data from the commission statement
   *
   * @param data - The raw data array
   * @returns This instance for method chaining
   */
  public setData(data: any): this {
    this.data = data;
    return this;
  }

  /**
   * Get the formatted/processed data
   *
   * @returns The formatted data
   */
  public getDataFormatted(): any {
    return this.dataFormatted;
  }

  /**
   * Set the formatted/processed data
   *
   * @param dataFormatted - The formatted data
   * @returns This instance for method chaining
   */
  public setDataFormatted(dataFormatted: any): this {
    this.dataFormatted = dataFormatted;
    return this;
  }

  /**
   * Get the associated user
   *
   * @returns The user entity
   */
  public getUser(): User {
    return this.user;
  }

  /**
   * Set the associated user
   *
   * @param user - The user entity
   * @returns This instance for method chaining
   */
  public setUser(user: User): this {
    this.user = user;
    return this;
  }

  /**
   * Get the associated commission statement mapping
   *
   * @returns The mapping configuration
   */
  public getCommissionStatementMapping(): CommissionStatementMapping {
    return this.commissionStatementMapping;
  }

  /**
   * Set the associated commission statement mapping
   *
   * @param commissionStatementMapping - The mapping configuration
   * @returns This instance for method chaining
   */
  public setCommissionStatementMapping(
    commissionStatementMapping: CommissionStatementMapping
  ): this {
    this.commissionStatementMapping = commissionStatementMapping;
    return this;
  }

  /**
   * Get the creation date and time
   *
   * @returns The creation date
   */
  public getDateTimeCreated(): Date {
    return this.dateTimeCreated;
  }

  /**
   * Set the creation date and time
   *
   * @param dateTimeCreated - The creation date
   * @returns This instance for method chaining
   */
  public setDateTimeCreated(dateTimeCreated: Date): this {
    this.dateTimeCreated = dateTimeCreated;
    return this;
  }

  /**
   * Get the statement date
   *
   * @returns The statement date
   */
  public getDate(): Date {
    return this.date;
  }

  /**
   * Set the statement date
   *
   * @param date - The statement date
   * @returns This instance for method chaining
   */
  public setDate(date: Date): this {
    this.date = date;
    return this;
  }

  /**
   * Get the array of ignored row numbers
   *
   * @returns Array of row numbers to ignore or null
   */
  public getIgnoredRows(): Array<number> | null {
    return this.ignoredRows;
  }

  /**
   * Set the array of ignored row numbers
   *
   * @param ignoredRows - Array of row numbers to ignore
   * @returns This instance for method chaining
   */
  public setIgnoredRows(ignoredRows: Array<number> | null): this {
    this.ignoredRows = ignoredRows;
    return this;
  }

  /**
   * Get the header row number
   *
   * @returns The header row number
   */
  public getHeaderRow(): number {
    return this.headerRow;
  }

  /**
   * Set the header row number
   *
   * @param headerRow - The header row number
   * @returns This instance for method chaining
   */
  public setHeaderRow(headerRow: number): this {
    this.headerRow = headerRow;
    return this;
  }

  // Data Processing Methods

  /**
   * Get clean data with header and ignored rows filtered out
   *
   * This method returns the commission statement data with all ignored rows
   * and header rows removed, providing only the actual data rows for processing.
   *
   * @returns Array of data rows excluding headers and ignored rows
   */
  public getCleanData(): Array<Array<any>> {
    const cleanData = this.data;

    // Define the full list of ignored rows
    const excludedRows = [];

    // Add the user-defined ignored rows
    if (this.getIgnoredRows()) {
      excludedRows.push(...this.getIgnoredRows());
    } else if (
      this.commissionStatementMapping &&
      this.commissionStatementMapping.getIgnoredRows()
    ) {
      excludedRows.push(...this.commissionStatementMapping.getIgnoredRows());
    }

    // Add the header row
    if (this.getHeaderRow()) {
      excludedRows.push(this.getHeaderRow());
    } else if (this.commissionStatementMapping && this.commissionStatementMapping.getHeaderRow()) {
      excludedRows.push(this.commissionStatementMapping.getHeaderRow());
    }

    // Filter out excluded rows and return clean data
    return cleanData.filter((_, index) => !excludedRows.includes(index));
  }

  /**
   * Get aggregated data for a specific field
   *
   * Extracts and returns unique values from a specific column/field in the
   * commission statement data. Useful for getting lists of unique agents,
   * companies, or other dimensional data.
   *
   * @param fieldName - Name of the field to aggregate data for
   * @returns Array of unique values for the specified field, or null if not found
   */
  public getAggregatedData(fieldName: CommissionStatementMappingFieldName): Array<any> {
    const cleanData = this.getCleanData();

    // Ensure we have a mapping configuration
    if (!this.commissionStatementMapping) {
      return null;
    }

    // Get the column index for the specified field
    const column = this.commissionStatementMapping.getColumnByFieldName(fieldName);

    if (!column) {
      return null;
    }

    // Extract unique values from the specified column
    const uniqueValues = [
      ...new Set(
        cleanData
          .filter((item) => item[column]) // Filter out empty/null values
          .map((item) => item[column]) // Extract column values
      ),
    ];

    return uniqueValues;
  }
}
